/**
 * Client-side mirror of functions/src/asi/asiRoleResolver.js pure helpers.
 * Keep in sync with the server resolver (scope containment + role permissions).
 */

export const SCOPE_TYPES = {
  interaction: "interaction",
  businessUnit: "businessUnit",
  org: "org",
  platform: "platform",
};

export const ROLES = {
  trainer: "trainer",
  interactionManager: "interactionManager",
  asiReadinessLead: "asiReadinessLead",
  globalManager: "globalManager",
};

export const PLATFORM_SCOPE_ID = "platform";

function interactionOrgId(interaction) {
  if (!interaction) return null;
  return interaction.institutionId || interaction.organizationId || null;
}

function interactionIdOf(interaction) {
  if (!interaction) return null;
  return interaction.interactionId || interaction.id || null;
}

/**
 * Whether a single role assignment covers the given Interaction.
 * Containment: org contains businessUnit contains interaction.
 */
export function assignmentCoversInteraction(assignment, interaction) {
  if (!assignment || !interaction) return false;

  const { scopeType, scopeId } = assignment;
  if (!scopeType || scopeId == null || scopeId === "") return false;

  const orgId = interactionOrgId(interaction);
  if (assignment.institutionId && orgId && assignment.institutionId !== orgId) {
    return false;
  }

  if (scopeType === SCOPE_TYPES.org) {
    return Boolean(orgId) && scopeId === orgId;
  }

  if (scopeType === SCOPE_TYPES.businessUnit) {
    return (
      Boolean(interaction.businessUnitId) &&
      scopeId === interaction.businessUnitId
    );
  }

  if (scopeType === SCOPE_TYPES.interaction) {
    const id = interactionIdOf(interaction);
    return Boolean(id) && scopeId === id;
  }

  // platform / GlobalManager never covers Interactions by containment.
  return false;
}

/**
 * Aggregate permissions from role assignments for one Interaction.
 * @returns {{ canViewAnnotate: boolean, canApprove: boolean, canViewReadiness: boolean, inScope: boolean }}
 */
export function resolvePermissionsFromAssignments(assignments, interaction) {
  const list = Array.isArray(assignments) ? assignments : [];
  let canViewAnnotate = false;
  let canApprove = false;
  let canViewReadiness = false;
  let inScope = false;

  for (const assignment of list) {
    if (!assignmentCoversInteraction(assignment, interaction)) continue;

    if (assignment.role === ROLES.trainer) {
      inScope = true;
      canViewAnnotate = true;
      // canViewReadiness remains false unless a readiness-lead grant also matches
    } else if (assignment.role === ROLES.interactionManager) {
      inScope = true;
      canViewAnnotate = true;
      canApprove = true;
      // canViewReadiness remains false unless a readiness-lead grant also matches
    } else if (assignment.role === ROLES.asiReadinessLead) {
      // Readiness is org-level; does not grant Interaction-canvas inScope/annotate/approve.
      canViewReadiness = true;
    }
  }

  return { canViewAnnotate, canApprove, canViewReadiness, inScope };
}

/**
 * Build accessByNodeId map for a workflow graph.
 */
export function buildAccessByNodeId({ nodes, interactionsById, assignments }) {
  const access = {};
  const list = Array.isArray(nodes) ? nodes : [];
  const byId = interactionsById || {};

  for (const node of list) {
    if (!node?.nodeId) continue;
    const interactionId = node.interactionId;
    const interaction = byId[interactionId] || {
      interactionId,
      institutionId: node.institutionId || null,
      businessUnitId: node.businessUnitId || null,
    };
    access[node.nodeId] = resolvePermissionsFromAssignments(
      assignments,
      interaction,
    );
  }

  return access;
}
